'use client';

import React, { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export type AdPlacement = 'banner' | 'sidebar' | 'between-videos' | 'video-post' | 'blog-post';

interface Ad {
  id: string;
  title: string;
  advertiser: string;
  image_url: string;
  image_alt: string;
  link_url: string;
  placement: AdPlacement;
}

interface AdDisplayProps {
  placement: AdPlacement;
  className?: string;
}

export default function AdDisplay({ placement, className = '' }: AdDisplayProps) {
  const [ads, setAds] = useState<Ad[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const supabase = createClient();

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const { data } = await supabase
          .from('ads')
          .select('id, title, advertiser, image_url, image_alt, link_url, placement')
          .eq('placement', placement)
          .eq('is_active', true);
        if (data && data.length > 0) {
          setAds(data);
        }
      } catch {
        // silently fail — ads are non-critical
      } finally {
        setLoaded(true);
      }
    };
    fetchAds();
  }, [placement]);

  // Rotate ads every 8 seconds if multiple
  useEffect(() => {
    if (ads.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % ads.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [ads.length]);

  if (!loaded || ads.length === 0) return null;

  const ad = ads[currentIndex];

  if (placement === 'banner') {
    return (
      <div className={`w-full ${className}`}>
        <div className="relative group">
          <span className="absolute top-1 left-1 z-10 text-[9px] uppercase tracking-widest text-text-muted bg-surface-primary/80 px-1 py-0.5 rounded-sm leading-none">
            Ad
          </span>
          <a
            href={ad.link_url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            aria-label={`Advertisement: ${ad.title}`}
            className="block overflow-hidden border border-stroke-primary hover:border-stroke-light transition-colors"
          >
            <img
              src={ad.image_url}
              alt={ad.image_alt || ad.title}
              className="w-full object-cover max-h-[200px] min-h-[100px] sm:max-h-[250px] sm:min-h-[160px] group-hover:opacity-90 transition-opacity"
            />
          </a>
          {/* dots removed */}
        </div>
      </div>
    );
  }

  if (placement === 'sidebar') {
    return (
      <div className={`w-full ${className}`}>
        <div className="border border-stroke-primary bg-surface-secondary">
          <div className="px-3 py-1.5 border-b border-stroke-primary flex items-center justify-between">
            <span className="text-[9px] uppercase tracking-widest text-text-muted">Sponsored</span>
            {ad.advertiser && (
              <span className="text-[9px] text-text-muted truncate ml-2">{ad.advertiser}</span>
            )}
          </div>
          <a
            href={ad.link_url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            aria-label={`Advertisement: ${ad.title}`}
            className="block group"
          >
            <img
              src={ad.image_url}
              alt={ad.image_alt || ad.title}
              className="w-full object-cover min-h-[160px] max-h-[260px] sm:min-h-[200px] sm:max-h-[320px] group-hover:opacity-90 transition-opacity"
            />
            {ad.title && (
              <div className="px-3 py-3">
                <p className="text-text-secondary text-sm line-clamp-2 group-hover:text-text-primary transition-colors">
                  {ad.title}
                </p>
              </div>
            )}
          </a>
        </div>
      </div>
    );
  }

  // video-post and blog-post — full-width ad below content
  if (placement === 'video-post' || placement === 'blog-post') {
    return (
      <div className={`w-full ${className}`}>
        <div className="relative group border border-stroke-primary overflow-hidden">
          <span className="absolute top-1 left-1 z-10 text-[9px] uppercase tracking-widest text-text-muted bg-surface-primary/80 px-1 py-0.5 rounded-sm leading-none">
            Ad
          </span>
          <a
            href={ad.link_url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            aria-label={`Advertisement: ${ad.title}`}
            className="flex flex-col sm:flex-row items-stretch sm:items-center gap-0 sm:gap-5 hover:bg-surface-secondary transition-colors"
          >
            <img
              src={ad.image_url}
              alt={ad.image_alt || ad.title}
              className="w-full sm:w-48 h-44 sm:h-36 object-cover flex-shrink-0 group-hover:opacity-90 transition-opacity"
            />
            <div className="flex-1 min-w-0 p-4 sm:py-4 sm:pr-4 sm:pl-0">
              {ad.advertiser && (
                <p className="text-text-muted text-xs uppercase tracking-wide mb-1">{ad.advertiser}</p>
              )}
              <p className="text-text-primary text-base font-bold line-clamp-2">{ad.title}</p>
              <span className="inline-block mt-3 text-sm text-brand-red font-bold uppercase tracking-wide">
                Learn More →
              </span>
            </div>
          </a>
          {/* dots removed */}
        </div>
      </div>
    );
  }

  // between-videos
  return (
    <div className={`w-full ${className}`}>
      <div className="relative border border-stroke-primary overflow-hidden group">
        <span className="absolute top-2 left-2 z-10 text-[9px] uppercase tracking-widest text-text-muted bg-surface-primary/80 px-1.5 py-0.5 rounded-sm">
          Sponsored
        </span>
        <a
          href={ad.link_url}
          target="_blank"
          rel="noopener noreferrer sponsored"
          aria-label={`Advertisement: ${ad.title}`}
          className="flex flex-col sm:flex-row items-stretch sm:items-center gap-0 sm:gap-5 hover:bg-surface-secondary transition-colors"
        >
          <img
            src={ad.image_url}
            alt={ad.image_alt || ad.title}
            className="w-full sm:w-48 h-44 sm:h-32 object-cover flex-shrink-0 group-hover:opacity-90 transition-opacity"
          />
          <div className="flex-1 min-w-0 p-4 sm:py-4 sm:pr-4 sm:pl-0">
            {ad.advertiser && (
              <p className="text-text-muted text-xs uppercase tracking-wide mb-1">{ad.advertiser}</p>
            )}
            <p className="text-text-primary text-base font-bold line-clamp-2">{ad.title}</p>
            <span className="inline-block mt-3 text-sm text-brand-red font-bold uppercase tracking-wide">
              Learn More →
            </span>
          </div>
        </a>
      </div>
    </div>
  );
}
